import { useAgeDropdown } from "@/helpers/hooks/useAgeDropdown.ts";
import { useEngineStore } from "@/stores/EngineStore/EngineStore.ts";
import { AdultType } from "@/stores/EngineStore/interfaces/AdultType.ts";
import { ChevronDown, User } from "lucide-react";
import { createPortal } from "react-dom";

const ageOptions = Array.from({ length: 82 }, (_, i) => ({
  value: i + 18,
  label: String(i + 18),
}));

export const AdultAgeDropdown = ({
  adult,
  cartItemId,
  compact,
  label,
}: {
  adult: AdultType;
  cartItemId: string;
  compact?: boolean;
  label?: string;
}) => {
  const setCartItemAdultAge = useEngineStore((state) => state.setCartItemAdultAge);

  const {
    isOpen,
    isEditing,
    inputValue,
    highlightedIndex,
    dropdownStyle,
    filteredOptions,
    dropdownRef,
    triggerRef,
    inputRef,
    containerEl,
    open,
    handleSelect,
    handleKeyDown,
    handleInputChange,
    commitTypedValue,
    setHighlightedIndex,
  } = useAgeDropdown({
    options: ageOptions,
    minAge: 18,
    maxAge: 99,
    onSelect: (age) => setCartItemAdultAge(adult.id, age, cartItemId),
  });

  const currentLabel = adult.age == null ? "—" : String(adult.age);

  return (
    <div className={`c-child-age-input${compact ? " c-child-age-input--compact" : ""}`}>
      {!compact && (
        <div className="c-child-age-input__icon">
          <User size={12} />
        </div>
      )}
      <label className="c-be-number-input-label">{label}</label>
      <div
        ref={triggerRef}
        className={`c-child-age-input__select-wrap${isOpen ? " is-open" : ""}`}
        onClick={open}
      >
        {isEditing ? (
          <input
            ref={inputRef}
            className="c-child-age-input__input"
            type="text"
            inputMode="numeric"
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onBlur={commitTypedValue}
            placeholder={currentLabel}
            onClick={(e) => e.stopPropagation()}
          />
        ) : (
          <span className="c-child-age-input__value">{currentLabel}</span>
        )}
        <ChevronDown size={14} className="c-child-age-input__chevron" />
      </div>
      {isOpen &&
        createPortal(
          <div
            ref={dropdownRef}
            className="c-child-age-input__dropdown"
            style={dropdownStyle}
            onMouseDown={(e) => e.preventDefault()}
          >
            {filteredOptions.map((opt, idx) => (
              <div
                key={opt.value}
                data-value={opt.value}
                className={`c-child-age-input__option${
                  opt.value === (adult.age ?? 20) ? " c-child-age-input__option--selected" : ""
                }${idx === highlightedIndex ? " c-child-age-input__option--highlighted" : ""}`}
                onClick={(e) => {
                  e.stopPropagation();
                  handleSelect(opt.value);
                }}
                onMouseEnter={() => setHighlightedIndex(idx)}
              >
                {opt.label}
              </div>
            ))}
          </div>,
          containerEl,
        )}
    </div>
  );
};
